import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, FileText } from 'lucide-react';
import {
  labOrderService,
  labOrderItemService,
  labResultService,
  labReportService,
  generateLabReportPDFBlob,
  notifyReportReady,
  LabOrder,
  LabOrderItem,
  LabResult
} from '../../lib/services.lab';
import { paymentVerificationService } from '../../lib/services.paymentVerification';
import { useStore } from '../../lib/store';
import { getUserRoleFromDB } from '../../lib/rbac';
import toast from 'react-hot-toast';

export default function VerifyFinalize() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { user } = useStore();
  const [order, setOrder] = useState<LabOrder | null>(null);
  const [orderItems, setOrderItems] = useState<LabOrderItem[]>([]);
  const [results, setResults] = useState<Record<string, LabResult[]>>({});
  const [loading, setLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [canVerify, setCanVerify] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'partial' | 'paid'>('pending');
  const [canRelease, setCanRelease] = useState(false);

  useEffect(() => {
    checkPermissions();
    if (orderId) {
      loadOrder();
    }
  }, [orderId]);

  const checkPermissions = async () => {
    if (!user?.id) return;
    try {
      const role = await getUserRoleFromDB(user.id);
      setCanVerify(role === 'admin' || role === 'pathologist');
      setCanRelease(role === 'admin' || role === 'receptionist');
    } catch (error) {
      console.error('Error checking permissions:', error);
    }
  };

  const loadOrder = async () => {
    if (!orderId) return;

    try {
      const orderData = await labOrderService.getById(orderId);
      if (!orderData) {
        toast.error('Order not found');
        navigate('/lab');
        return;
      }

      setOrder(orderData);

      const items = await labOrderItemService.getByOrder(orderId);
      setOrderItems(items);

      const resultsData: Record<string, LabResult[]> = {};
      for (const item of items) {
        const itemResults = await labResultService.getByOrderItem(item.id);
        resultsData[item.id] = itemResults;
      }

      setResults(resultsData);

      if (orderId) {
        const paymentCheck = await paymentVerificationService.verifyLabOrderPayment(orderId);
        setPaymentStatus(paymentCheck.paymentStatus);
      }
    } catch (error) {
      console.error('Failed to load order:', error);
      toast.error('Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyResult = async (resultId: string) => {
    if (!user?.id) return;

    try {
      await labResultService.verify(resultId, user.id);
      toast.success('Result verified');
      await loadOrder();
    } catch (error) {
      console.error('Failed to verify result:', error);
      toast.error('Failed to verify result');
    }
  };

  const handleFinalizeReport = async (forceRelease: boolean = false) => {
    if (!user?.id || !orderId || !order) return;

    const allVerified = orderItems.every(item => {
      const itemResults = results[item.id] || [];
      return itemResults.every(r => r.verified_by);
    });

    if (!allVerified) {
      toast.error('All results must be verified before finalizing');
      return;
    }

    if (paymentStatus !== 'paid' && !forceRelease) {
      toast.error('Payment is pending. Report cannot be finalized. Admin/Billing can force release.');
      return;
    }

    setFinalizing(true);

    try {
      const reportNo = await labReportService.generateReportNumber();
      
      const pdfBlob = await generateLabReportPDFBlob({
        reportNo,
        patient: order.patient,
        orderId,
        orderItems,
        results,
        finalizedBy: user.id,
        finalizedAt: new Date()
      });

      const fileName = await labReportService.uploadPDF(orderId, pdfBlob);
      const signedUrl = await labReportService.getSignedUrl(fileName, 31536000);

      const reportData: any = {
        order_id: orderId,
        report_no: reportNo,
        pdf_url: signedUrl,
        finalized_by: user.id,
        finalized_at: new Date().toISOString()
      };

      if (paymentStatus !== 'paid') {
        reportData.payment_hold = true;
      }

      const createdReport = await labReportService.create(reportData);

      if (forceRelease && createdReport?.id) {
        await paymentVerificationService.releaseLabReport(createdReport.id, user.id);
      }

      for (const item of orderItems) {
        await labOrderItemService.updateStatus(item.id, 'finalized');
      }

      await labOrderService.updateStatus(orderId, 'finalized');
      
      if (order?.patient) {
        await notifyReportReady(
          order.patient_id,
          order.patient.name,
          orderId,
          orderId
        );
      }

      if (forceRelease) {
        toast.success('Report finalized and released (payment pending)');
      } else {
        toast.success('Report finalized successfully');
      }
      navigate('/lab');
    } catch (error) {
      console.error('Failed to finalize report:', error);
      toast.error('Failed to finalize report');
    } finally {
      setFinalizing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!canVerify) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">You don't have permission to verify results.</p>
      </div>
    );
  }

  const allVerified = orderItems.every(item => {
    const itemResults = results[item.id] || [];
    return itemResults.length > 0 && itemResults.every(r => r.verified_by);
  });

  return (
    <section className="space-y-6">
      <header className="flex items-center gap-4">
        <button
          onClick={() => navigate('/lab')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Verify & Finalize</h1>
          <p className="text-sm text-neutral-600">
            Order #{orderId?.substring(0, 8)} - {order?.patient?.name}
          </p>
        </div>
      </header>

      {/* Verification Status */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Verification Status</h3>
            <p className="text-sm text-gray-600 mt-1">
              {allVerified ? 'All results verified' : 'Verification in progress'}
            </p>
          </div>
          {allVerified && (
            <CheckCircle className="w-8 h-8 text-green-600" />
          )}
        </div>
      </div>

      {/* Results Review */}
      <div className="space-y-6">
        {orderItems.map((item) => (
          <div key={item.id} className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4">
              {item.test?.name} ({item.test?.code})
            </h3>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Analyte</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Flag</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(results[item.id] || []).map((result) => (
                    <tr key={result.id}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {result.analyte}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                        {result.value}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {result.unit}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {result.ref_low} - {result.ref_high}
                      </td>
                      <td className="px-4 py-3">
                        {result.flag && (
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            result.flag === 'L' ? 'bg-blue-100 text-blue-800' :
                            result.flag === 'H' ? 'bg-red-100 text-red-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {result.flag === 'L' ? 'Low' : result.flag === 'H' ? 'High' : 'Normal'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {result.verified_by ? (
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            Verified
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {!result.verified_by && (
                          <button
                            onClick={() => handleVerifyResult(result.id)}
                            className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                          >
                            Verify
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      {/* Payment Status Warning */}
      {paymentStatus !== 'paid' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Payment Pending</h3>
              <p className="mt-1 text-sm text-yellow-700">
                This order has {paymentStatus === 'partial' ? 'partial' : 'no'} payment. Report will be held until payment is complete.
                {canRelease && ' As an admin/billing user, you can force release the report.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 justify-end">
        <button
          onClick={() => navigate('/lab')}
          className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Back to Worklist
        </button>
        {paymentStatus !== 'paid' && canRelease ? (
          <button
            onClick={() => handleFinalizeReport(true)}
            disabled={!allVerified || finalizing}
            className="inline-flex items-center px-6 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileText className="w-4 h-4 mr-2" />
            {finalizing ? 'Releasing...' : 'Force Release Report'}
          </button>
        ) : (
          <button
            onClick={() => handleFinalizeReport(false)}
            disabled={!allVerified || finalizing || paymentStatus !== 'paid'}
            className="inline-flex items-center px-6 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileText className="w-4 h-4 mr-2" />
            {finalizing ? 'Finalizing...' : 'Finalize Report'}
          </button>
        )}
      </div>
    </section>
  );
}
